'use client';
import React from 'react';
import { BaristaOrder } from './baristaData';
import Icon from '@/components/ui/AppIcon';

interface BaristaOrderCardProps {
  order: BaristaOrder;
  onAdvance: (id: string) => void;
  onComplete: (id: string) => void;
  onSelect: (order: BaristaOrder) => void;
}

const elapsedColor = (min: number) => {
  if (min >= 10) return 'text-red-400';
  if (min >= 5) return 'text-amber-400';
  return 'text-green-400';
};

export default function BaristaOrderCard({ order, onAdvance, onComplete, onSelect }: BaristaOrderCardProps) {
  return (
    <div
      className={`bg-white/5 border rounded-2xl p-4 cursor-pointer hover:bg-white/10 transition-all duration-150 active:scale-[0.99] ${
        order.elapsedMinutes >= 10
          ? 'border-red-500/40'
          : order.elapsedMinutes >= 5
          ? 'border-amber-500/30' :'border-white/10'
      }`}
      onClick={() => onSelect(order)}
    >
      {/* Card header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-xs">{order.customerInitials}</span>
          </div>
          <div>
            <p className="text-white font-bold text-sm">{order.orderNumber}</p>
            <p className="text-white/60 text-xs">{order.customerName}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <span className={`text-xs font-bold font-tabular ${elapsedColor(order.elapsedMinutes)}`}>
            {order.elapsedMinutes}dk
          </span>
          <span className="text-white/40 text-[10px]">{order.createdAt}</span>
        </div>
      </div>

      {/* Order type badge */}
      <div className="flex items-center gap-2 mb-3">
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
          order.orderType === 'gel-al' ?'bg-blue-500/20 text-blue-400' :'bg-purple-500/20 text-purple-400'
        }`}>
          {order.orderType === 'gel-al' ? '🏃 Gel Al' : `🪑 Masa ${order.tableNumber}`}
        </span>
        {order.elapsedMinutes >= 8 && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">
            ⚠ Gecikiyor
          </span>
        )}
      </div>

      {/* Items */}
      <div className="space-y-1.5 mb-3">
        {order.items.map((item) => (
          <div key={item.id} className="flex items-start gap-2">
            <span className="text-white/30 text-xs mt-0.5 flex-shrink-0">×{item.quantity}</span>
            <div className="min-w-0">
              <p className="text-white/90 text-xs font-semibold leading-tight">{item.name}</p>
              {item.name !== 'Tereyağlı Kruvasan' && item.name !== 'Cheesecake Dilimi' && item.name !== 'Fıstıklı Kek' && (
                <p className="text-white/40 text-[10px] leading-tight">
                  {item.size} · {item.milk}
                  {item.syrup !== 'Yok' && ` · ${item.syrup}`}
                  {item.extraShot && ' · +Ekstra Shot'}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Notes */}
      {order.notes && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2 mb-3">
          <p className="text-amber-300 text-xs">
            <span className="font-bold">Not: </span>{order.notes}
          </p>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
        {order.status === 'yeni' && (
          <button
            onClick={() => onAdvance(order.id)}
            className="flex-1 bg-blue-500/20 border border-blue-500/30 text-blue-400 font-bold text-xs py-2.5 rounded-xl hover:bg-blue-500/30 transition-colors active:scale-[0.97]"
          >
            Hazırlamaya Başla
          </button>
        )}
        {order.status === 'hazirlaniyor' && (
          <button
            onClick={() => onAdvance(order.id)}
            className="flex-1 bg-green-500/20 border border-green-500/30 text-green-400 font-bold text-xs py-2.5 rounded-xl hover:bg-green-500/30 transition-colors active:scale-[0.97]"
          >
            Hazır ✓
          </button>
        )}
        {order.status === 'hazir' && (
          <button
            onClick={() => onComplete(order.id)}
            className="flex-1 bg-primary/30 border border-primary/40 text-green-300 font-bold text-xs py-2.5 rounded-xl hover:bg-primary/40 transition-colors active:scale-[0.97]"
          >
            Teslim Edildi ✓✓
          </button>
        )}
        <button
          onClick={() => {}}
          className="w-9 h-9 bg-white/5 border border-white/10 rounded-xl flex items-center justify-center hover:bg-white/10 transition-colors"
        >
          <Icon name="EllipsisVerticalIcon" size={16} className="text-white/50" />
        </button>
      </div>
    </div>
  );
}